import { action, observable, makeObservable } from "mobx";
import { api } from "../lib/api";
import { userStore } from "./";
import { appHistory } from "../appHistory";
import { ApiError } from "../models/api";
import {
  IApiMagicLinkLoginRequest,
  IApiResponseErrors
} from "../models/apiResponses";
import { LoginPageScreen } from "./types";
import { SharedStateStore } from "./SharedStateStore";

export interface ILoginPageUser {
  email?: string;
  username?: string;
  password?: string;
}

const NO_ERRORS: Required<IApiResponseErrors> & {
  username: string[];
  password: string[];
  email: string[];
} = {
  non_field_errors: [],
  username: [],
  password: [],
  email: []
};

export class LoginPageStore extends SharedStateStore {
  sendingEmail = false;

  loggingIn = false;

  activeScreen: LoginPageScreen = LoginPageScreen.HomeScreen;

  user: ILoginPageUser = {
    email: "",
    password: ""
  };

  errorCode: number | undefined;

  errors = {
    ...NO_ERRORS
  };

  constructor() {

    super()
    makeObservable(this, {
      sendingEmail: observable,
      loggingIn: observable,
      activeScreen: observable,
      user: observable,
      errorCode: observable,
      errors: observable,
      resetLoginScreen: action
    });
  }

  resetLoginScreen() {
    this.activeScreen = LoginPageScreen.UseLoginEmail;
  }

  async sendLoginEmail() {
    if (this.sendingEmail) return;

    this.setState({
      errors: { ...NO_ERRORS }
    })
    if (!this.user.email) {
      this.setState({
        errors: {
          ...this.errors,
          email: ["You need to specify an email address"]
        }
      })
      return;
    }

    try {
      this.setState({ sendingEmail: true })
      await api.create<IApiResponseErrors, IApiMagicLinkLoginRequest>(
        "login/magic-link",
        {
          emailAddress: this.user.email
        },
        {
          dontIncludeJwt: true
        }
      );
      this.setState({
        activeScreen: LoginPageScreen.LoginEmailSent
      })
    } catch (error) {
      this.setState({
        errors: {
          ...this.errors,
          ...((error as ApiError).errorPayload as IApiResponseErrors)
        },
        errorCode: (error as ApiError).statusCode
      })
    } finally {
      this.setState({ sendingEmail: false })
    }
  }

  async sendResetPasswordEmail() {
    if (this.sendingEmail) return;

    this.setState({
      errors: { ...NO_ERRORS }
    })
    this.setState({ sendingEmail: true })

    try {
      await api.create(
        "password-reset",
        { email: this.user.email },
        {
          dontIncludeJwt: true
        }
      );
      this.setState({
        activeScreen: LoginPageScreen.ResetPasswordEmailSent
      })
    } catch (error) {
      this.setState({
        errors: {
          ...this.errors,
          ...((error as ApiError).errorPayload as IApiResponseErrors)
        },
        errorCode: (error as ApiError).statusCode
      })
    } finally {
      this.setState({ sendingEmail: false })
    }
  }

  async login() {
    if (this.loggingIn) return;

    this.setState({
      errors: { ...NO_ERRORS }
    })
    this.setState({ loggingIn: true })

    try {
      let user = await userStore.login(this.user);
      let returnUrl = this.getReturnUrl();
      if (returnUrl === "" || returnUrl === "/" || returnUrl.includes("landing-page")) {
        if (user.districtContentEnabled) returnUrl = "/mydistrict"
        if (user.schoolContentEnabled) returnUrl = "/myschool"
      }
      this.resetLoginDetails();
      appHistory.push(returnUrl);
    } catch (error) {
      this.setState({
        errors: {
          ...this.errors,
          ...((error as ApiError).errorPayload as IApiResponseErrors)
        },
        errorCode: (error as ApiError).statusCode
      })
    } finally {
      this.setState({ loggingIn: false })
    }
  }

  setReturnUrl(url: string): void {
    let expireDate = new Date();
    expireDate.setTime(expireDate.getTime() + 24 * 60 * 60 * 1000);
    document.cookie = `returnUrl=${url}; expires=${expireDate.toUTCString()}; path=/`;
  }

  getReturnUrl(): string {
    let name = "returnUrl=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let cookies = decodedCookie.split(";");

    let foundUrl = "";
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];

      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1);
      }

      if (cookie.indexOf(name) === 0) {
        foundUrl = cookie.substring(name.length, cookie.length);
        break;
      }
    }

    if (foundUrl === "/login") {
      foundUrl = "";
    }

    return foundUrl;
  }

  resetLoginDetails() {
    this.setState({
      user: {
        email: "",
        password: ""
      }
    })
  }
}
